"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_LOYALTY_DISCOUNT_DISPLAY = exports.LoyaltyDiscountDisplay = exports.LOYALTY_SHOW_OLD_PRICE_KEY = exports.LOYALTY_DISCOUNT_DISPLAY_KEY = exports.LoyaltyDiscountDisplayMode = exports.ReceiptConfigBootstrap = exports.EffectiveReceiptConfig = exports.ReceiptConfigSource = exports.ReceiptConfigPutRequest = exports.ReceiptConfigRow = exports.ReceiptConfigJson = exports.ReceiptConfigScope = exports.ReceiptType = void 0;
exports.readLoyaltyDiscountDisplay = readLoyaltyDiscountDisplay;
const zod_1 = require("zod");
/**
 * Canonical receipt-configuration contracts. Mirrors the BOS-side
 * `receipt_configs` table + the resolved effective shape returned
 * by `/receipt-configs/effective/:terminalId/:receiptType`, plus the
 * `receiptConfig` field added to the terminal bootstrap response.
 *
 * See apps/bos/docs/modules/receipt-config.md.
 */
exports.ReceiptType = zod_1.z.enum([
    'sale',
    'refund',
    'void',
    'parked',
    'x_report',
    'z_report',
    'copy',
]);
exports.ReceiptConfigScope = zod_1.z.enum(['customer', 'location', 'terminal']);
/**
 * The `json_config` blob. Free-form by zod (the lock validator on
 * the BOS side enforces the shape against the active pack's
 * `manifest.receiptTemplate.elements[].configurableFields`). Sprint 3
 * baseline keys are documented in receipt-config.md.
 */
exports.ReceiptConfigJson = zod_1.z.record(zod_1.z.string(), zod_1.z.union([zod_1.z.string(), zod_1.z.number(), zod_1.z.boolean()]));
/** Wire shape for one row in the `receipt_configs` table. */
exports.ReceiptConfigRow = zod_1.z.object({
    id: zod_1.z.string(),
    scopeType: exports.ReceiptConfigScope,
    scopeId: zod_1.z.string().nullable(),
    receiptType: exports.ReceiptType,
    countryPackId: zod_1.z.string(),
    packVersion: zod_1.z.string(),
    jsonConfig: exports.ReceiptConfigJson,
    effectiveAt: zod_1.z.string().datetime().nullable(),
    version: zod_1.z.number().int().positive(),
    createdAt: zod_1.z.string().datetime(),
});
exports.ReceiptConfigPutRequest = zod_1.z.object({
    scopeType: exports.ReceiptConfigScope,
    scopeId: zod_1.z.string().nullable(),
    receiptType: exports.ReceiptType,
    jsonConfig: exports.ReceiptConfigJson,
});
/**
 * One entry in the `sourceTrail` array returned by the resolution
 * endpoint. Records which scope contributed which keys to the merged
 * effective config — lets the Sprint 10 editor render "value came
 * from customer default; override?" hints without a backend change.
 */
exports.ReceiptConfigSource = zod_1.z.object({
    scopeType: exports.ReceiptConfigScope,
    scopeId: zod_1.z.string().nullable(),
    version: zod_1.z.number().int().positive(),
    keys: zod_1.z.array(zod_1.z.string()).min(1),
});
/** Returned by `GET /receipt-configs/effective/:terminalId/:receiptType`. */
exports.EffectiveReceiptConfig = zod_1.z.object({
    terminalId: zod_1.z.string(),
    receiptType: exports.ReceiptType,
    countryPackId: zod_1.z.string(),
    packVersion: zod_1.z.string(),
    effectiveConfig: exports.ReceiptConfigJson,
    sourceTrail: zod_1.z.array(exports.ReceiptConfigSource),
});
/**
 * `receiptConfig` field added to `/sync/catalog/bootstrap` in
 * Sprint 3. One resolved effective config per receipt type, ready
 * for the terminal renderer to consume directly.
 */
exports.ReceiptConfigBootstrap = zod_1.z.object({
    sale: exports.ReceiptConfigJson,
    refund: exports.ReceiptConfigJson,
    void: exports.ReceiptConfigJson,
    parked: exports.ReceiptConfigJson,
    x_report: exports.ReceiptConfigJson,
    z_report: exports.ReceiptConfigJson,
    copy: exports.ReceiptConfigJson,
});
// ---------------------------------------------------------------------------
// Loyalty discount presentation (well-known receipt-config keys).
//
// How loyalty discounts are shown is a RETAILER + LEGAL choice, so it lives in
// receipt-config — which is already layered per **customer (tenant)** /
// location / terminal and per **country-pack**, giving the per-tenant + per
// -country control we need. The loyalty engine stays presentation-agnostic
// (it returns per-line discounts + a receipt discount total + receiptText);
// the terminal reads these keys to decide how to render them.
// ---------------------------------------------------------------------------
/**
 * Where/how the loyalty discount is shown on the customer display + receipt:
 * - `none`        — show only the final (already-discounted) price;
 * - `receipt_total` — show one total-discount line for the whole receipt;
 * - `per_line`    — show the discount on every affected line.
 */
exports.LoyaltyDiscountDisplayMode = zod_1.z.enum(['none', 'receipt_total', 'per_line']);
/** Config key: the discount display mode (string-valued). */
exports.LOYALTY_DISCOUNT_DISPLAY_KEY = 'loyalty.discountDisplay';
/** Config key: whether to print the pre-discount (old) price alongside (boolean). */
exports.LOYALTY_SHOW_OLD_PRICE_KEY = 'loyalty.showOldPrice';
/** Resolved loyalty discount-presentation settings. */
exports.LoyaltyDiscountDisplay = zod_1.z.object({
    mode: exports.LoyaltyDiscountDisplayMode,
    showOldPrice: zod_1.z.boolean(),
});
/** Default when a tenant/country sets nothing: a single receipt-total discount line, old price shown. */
exports.DEFAULT_LOYALTY_DISCOUNT_DISPLAY = {
    mode: 'receipt_total',
    showOldPrice: true,
};
/**
 * Read the loyalty discount-presentation settings from an effective
 * receipt-config blob, falling back to {@link DEFAULT_LOYALTY_DISCOUNT_DISPLAY}
 * for any key that is unset or invalid. Pure — safe on both BOS and terminal.
 */
function readLoyaltyDiscountDisplay(config) {
    const rawMode = config[exports.LOYALTY_DISCOUNT_DISPLAY_KEY];
    const parsedMode = exports.LoyaltyDiscountDisplayMode.safeParse(rawMode);
    const rawShow = config[exports.LOYALTY_SHOW_OLD_PRICE_KEY];
    return {
        mode: parsedMode.success ? parsedMode.data : exports.DEFAULT_LOYALTY_DISCOUNT_DISPLAY.mode,
        showOldPrice: typeof rawShow === 'boolean' ? rawShow : exports.DEFAULT_LOYALTY_DISCOUNT_DISPLAY.showOldPrice,
    };
}
//# sourceMappingURL=receipt-config.js.map